import { Box, Text } from '@chakra-ui/react';
import * as React from 'react';

import aiAskSourceStyles from '../../AIAskSources.module.css';
import type { AskAssistantMessage, AskMessage } from '../../types';
import type { AiAskIndexData } from '../../utils/aiAskHelpers';
import {
  filterSourcesByCitations,
  isNoContextAnswer,
} from '../../utils/aiAskHelpers';
import { SourcedAnswerBlock } from '../shared/SourcedAnswerBlock';
import { FormattedText } from '../shared/FormattedText';
import { SearchOnlyResultsBlock } from '../shared/SearchOnlyResultsBlock';
import { SourceCardsBlock } from '../shared/SourceCardsBlock';
import { FilteredListAnswer } from './FilteredListAnswer';
import { SearchRedirectAnswer } from './SearchRedirectAnswer';

export const AIAskUserMessage: React.FC<{ text: string }> = ({ text }) => (
  <Box
    bg="#1E78D7"
    color="white"
    borderRadius="14px"
    p="3"
    maxW="85%"
    boxShadow="sm"
  >
    <Text className={aiAskSourceStyles.askText}>{text}</Text>
  </Box>
);

const SourcedAssistantAnswer: React.FC<{
  message: AskAssistantMessage;
  indexData: AiAskIndexData;
  appOrigin: string;
}> = ({ message, indexData, appOrigin }) => {
  const hideSources = isNoContextAnswer(message.answer);
  const filteredSources = hideSources
    ? { sources: [], references: [] }
    : filterSourcesByCitations(
        message.answer,
        message.sources,
        message.references
      );

  return (
    <Box className="ai-ask-answer">
      <SourcedAnswerBlock
        answer={message.answer}
        sources={hideSources ? [] : message.sources || []}
        references={hideSources ? [] : message.references || []}
        appOrigin={appOrigin}
        indexData={indexData}
      />
      {!hideSources ? (
        <SourceCardsBlock
          sources={filteredSources.sources}
          references={filteredSources.references}
          appOrigin={appOrigin}
          indexData={indexData}
        />
      ) : null}
    </Box>
  );
};

export const AIAskAssistantMessage: React.FC<{
  message: AskAssistantMessage;
  indexData: AiAskIndexData;
  appOrigin: string;
  onViewResults: () => void;
  isViewResultsLoading: boolean;
}> = ({
  message,
  indexData,
  appOrigin,
  onViewResults,
  isViewResultsLoading,
}) => (
  <Box bg="#1676CE1A" borderRadius="14px" p="3" maxW="90%" boxShadow="sm">
    {message.mode === 'streaming' ? (
      <Box
        fontSize="sm"
        whiteSpace="pre-wrap"
        color="gray.800"
        className={aiAskSourceStyles.askText}
      >
        {message.answer ? (
          <FormattedText text={message.answer} />
        ) : (
          <Text
            as="span"
            color="gray.500"
            className={aiAskSourceStyles.askTyping}
          >
            ●●●
          </Text>
        )}
      </Box>
    ) : message.mode === 'error' ? (
      <Text fontSize="sm" color="red.600" className={aiAskSourceStyles.askText}>
        {message.answer || 'Something went wrong. Please try again.'}
      </Text>
    ) : message.responseType === 'search_redirect' ? (
      <SearchRedirectAnswer
        message={message}
        onSwitchToSearch={onViewResults}
        isLoading={isViewResultsLoading}
      />
    ) : message.responseType === 'filtered_list' ? (
      <FilteredListAnswer
        message={message}
        indexData={indexData}
        onViewResults={onViewResults}
        isLoading={isViewResultsLoading}
      />
    ) : message.mode === 'search_only' || message.answer == null ? (
      <>
        <Text fontSize="xs" color="gray.600" mb={2} className="ai-ask-meta">
          Search-only response
          {typeof message.debug?.search_confidence === 'number'
            ? ` · confidence ${Number(message.debug.search_confidence).toFixed(
                2
              )}`
            : ''}
        </Text>
        <SearchOnlyResultsBlock
          results={message.results}
          indexData={indexData}
        />
      </>
    ) : (
      <SourcedAssistantAnswer
        message={message}
        indexData={indexData}
        appOrigin={appOrigin}
      />
    )}
  </Box>
);

export const AIAskMessageRow: React.FC<{
  message: AskMessage;
  indexData: AiAskIndexData;
  appOrigin: string;
  onViewResults?: () => void;
  isViewResultsLoading?: boolean;
}> = ({
  message,
  indexData,
  appOrigin,
  onViewResults,
  isViewResultsLoading = false,
}) => (
  <Box
    w="100%"
    display="flex"
    justifyContent={message.role === 'user' ? 'flex-end' : 'flex-start'}
    className={`ai-ask-msg ai-ask-msg--${message.role}`}
  >
    {message.role === 'user' ? (
      <AIAskUserMessage text={message.text} />
    ) : (
      <AIAskAssistantMessage
        message={message}
        indexData={indexData}
        appOrigin={appOrigin}
        onViewResults={onViewResults ?? (() => undefined)}
        isViewResultsLoading={isViewResultsLoading}
      />
    )}
  </Box>
);
